import React from 'react';
import { captureError } from 'src/common/analytics';
import { flushLogs, logError } from 'src/common/frontendLog';

/**
 * App-wide error boundary. Catches any render/runtime error in the React tree
 * and shows a branded recovery page (Refresh + Go to homepage) instead of a
 * blank white screen.
 */
const FallbackStyles = () => (
  <style>{`
    .kerr-root{
      position:fixed; inset:0; z-index:9999; display:flex; align-items:center; justify-content:center;
      padding:24px; overflow:auto; font-family:'Inter',-apple-system,sans-serif;
      background:linear-gradient(135deg,#fcdfe9 0%,#fdeadd 36%,#efe8fb 70%,#f5f1fd 100%);
    }
    .kerr-card{
      position:relative; width:100%; max-width:480px; text-align:center;
      background:rgba(255,255,255,.72); backdrop-filter:blur(20px);
      border:1px solid rgba(255,255,255,.75); border-radius:24px;
      box-shadow:0 36px 90px -24px rgba(40,25,90,.38); padding:42px 38px 36px;
    }
    .kerr-ic{
      display:grid; place-items:center; width:74px; height:74px; margin:0 auto 22px; border-radius:20px;
      background:linear-gradient(135deg,#15123a 0%,#3a2f7a 60%,#6c5ce7 130%);
      box-shadow:0 18px 38px -14px rgba(54,40,120,.6);
    }
    .kerr-ic svg{ width:36px; height:36px; fill:#fff; }
    .kerr-title{ font-family:'Inter',sans-serif; font-size:24px; font-weight:700; color:#1c1747; margin:0 0 9px; letter-spacing:-.3px; }
    .kerr-sub{ font-size:14.5px; line-height:1.6; color:#54507a; margin:0 0 14px; }
    .kerr-actions{ display:flex; gap:12px; justify-content:center; flex-wrap:wrap; margin-top:30px; }
    .kerr-btn{
      display:inline-flex; align-items:center; gap:8px; cursor:pointer;
      height:48px; padding:0 22px; border-radius:13px; border:none;
      font-family:'Inter',sans-serif; font-size:15px; font-weight:700; letter-spacing:.2px; transition:all .16s ease;
    }
    .kerr-btn svg{ width:18px; height:18px; }
    .kerr-btn.primary{
      color:#fff; background:linear-gradient(135deg,#15123a 0%,#3a2f7a 60%,#6c5ce7 130%);
      box-shadow:0 14px 30px -10px rgba(54,40,120,.6);
    }
    .kerr-btn.primary:hover{ background:linear-gradient(135deg,#1c1850 0%,#473aa0 60%,#7a6bf0 130%); transform:translateY(-1px); }
    .kerr-btn.primary svg{ fill:#fff; }
    .kerr-btn.ghost{ color:#3a2f7a; background:#fff; border:1px solid rgba(108,92,231,.3); }
    .kerr-btn.ghost:hover{ background:#f6f3fc; border-color:rgba(108,92,231,.5); }
    .kerr-btn.ghost svg{ fill:#6c5ce7; }
  `}</style>
);

/**
 * The recovery screen itself.
 *
 * The copy is deliberately written for the cause users actually hit — the ECS
 * review services still powering on, which takes minutes — rather than for a
 * code fault. "Something went wrong" made a routine wait look like data loss.
 * Genuine render bugs land here too and get the same reassuring wording; the
 * "contact support" line is what covers them, and the real cause is still
 * captured to PostHog and to `lastGlobalError` by the boundary below.
 *
 * Exported so it can be opened directly at
 * `/status/error` to work on the UI without having to provoke a real crash —
 * see also `/status/crash`, which throws for real and exercises this through
 * the boundary (analytics capture, `lastGlobalError`, and all).
 */
export function ErrorFallback() {
  const refresh = () => window.location.reload();
  const goHome = () => window.location.assign('/');

  return (
    <div className="kerr-root">
      <FallbackStyles />
      <div className="kerr-card">
        <div className="kerr-ic">
          {/* A clock, not a warning triangle: by far the most common reason this
              screen appears is the review services still coming up, and an alarm
              icon reads as "your work is broken" when nothing is wrong. */}
          <svg viewBox="0 0 24 24">
            <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm.5-13H11v6l5.2 3.1.8-1.3-4.5-2.7V7z" />
          </svg>
        </div>
        <h1 className="kerr-title">Your workspace is starting up</h1>
        <p className="kerr-sub">
          The review services are warming up, which usually takes about five
          minutes. Nothing has been lost — your documents and reviews are safe.
          Refresh in a few minutes and you'll pick up right where you left off.
          If it still won't load after that, please contact support.
        </p>
        <div className="kerr-actions">
          <button className="kerr-btn primary" onClick={refresh}>
            <svg viewBox="0 0 24 24">
              <path d="M17.65 6.35A7.96 7.96 0 0 0 12 4a8 8 0 1 0 7.74 10h-2.08A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
            </svg>
            Refresh
          </button>
          <button className="kerr-btn ghost" onClick={goHome}>
            <svg viewBox="0 0 24 24">
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
            </svg>
            Go to homepage
          </button>
        </div>
      </div>
    </div>
  );
}

interface State {
  hasError: boolean;
  error: any;
}

class GlobalErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: any): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, info: any) {
    // eslint-disable-next-line no-console
    console.error('Global error boundary caught:', error, info);
    // Report to PostHog Error Tracking. React swallows render errors once a
    // boundary handles them, so window.onerror autocapture never sees this one —
    // it has to be reported explicitly.
    captureError('react_error_boundary', error, {
      component_stack: String(info?.componentStack ?? ''),
      url: window.location.href
    });
    // Also to the activity log, which is where you go to find out WHY this screen
    // appeared for a specific user at a specific time: the preceding entries in
    // their file are the failed requests that led here, sharing one outageId.
    logError('react_crash', {
      msg: String(error?.message ?? error),
      reason: 'error_boundary',
      extra: {
        stack: String(error?.stack ?? ''),
        componentStack: String(info?.componentStack ?? ''),
        href: window.location.href
      }
    });
    // The recovery screen's buttons reload or navigate away, either of which can
    // interrupt the batching timer — send now rather than hope.
    void flushLogs();
    // console.error is disabled in production (see App.tsx), which otherwise
    // swallows the real cause. Persist the last error so it can be inspected
    // after the fact — read `localStorage.getItem('lastGlobalError')`.
    try {
      localStorage.setItem(
        'lastGlobalError',
        JSON.stringify({
          message: String(error?.message ?? error),
          stack: String(error?.stack ?? ''),
          componentStack: String(info?.componentStack ?? ''),
          url: window.location.href,
          time: new Date().toISOString()
        })
      );
    } catch {
      /* ignore storage failures */
    }
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback />;
    }
    return this.props.children;
  }
}

export default GlobalErrorBoundary;
